import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Compliance docs hook. Loads vw_compliance_documents (which adds
// derived urgency + days_until_expiration) and exposes mutators.
//
// View is RLS-scoped, so SELECT only returns the caller's tenants.
// Inserts/updates go to the base table compliance_documents (also
// RLS-scoped).
export function useComplianceDocs() {
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const { data, error: dbErr } = await supabase
        .from('vw_compliance_documents')
        .select('*')
        // Order: docs without dates last, then by expiration ascending
        // so the most urgent surface at the top.
        .order('expiration_date', { ascending: true, nullsFirst: false })
      if (dbErr) throw dbErr
      setDocs(data || [])
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useComplianceDocs] load failed', e)
      setError(e?.message || 'Could not load compliance documents.')
    } finally {
      setLoading(false)
    }
  }

  async function addDocument(payload) {
    const { error: insertErr } = await supabase
      .from('compliance_documents')
      .insert(payload)
    if (insertErr) throw insertErr
    await load()
  }

  async function updateDocument(id, patch) {
    const { error: updateErr } = await supabase
      .from('compliance_documents')
      .update(patch)
      .eq('id', id)
    if (updateErr) throw updateErr
    await load()
  }

  async function archiveDocument(id) {
    return updateDocument(id, { archived: true })
  }

  useEffect(() => { load() }, [])

  // Derived counts the UI uses for headline summaries
  const counts = docs.reduce((acc, d) => {
    acc[d.urgency] = (acc[d.urgency] || 0) + 1
    return acc
  }, {})

  // Convenience subset: anything that needs attention this month.
  const needsAttention = docs.filter((d) =>
    d.urgency === 'expired' ||
    d.urgency === 'expires_in_7d' ||
    d.urgency === 'expires_in_30d'
  )

  return {
    docs,
    counts,
    needsAttention,
    loading,
    error,
    refresh: load,
    addDocument,
    updateDocument,
    archiveDocument,
  }
}

// Doc type vocabulary — keep in sync with the CHECK constraint in
// migration 0031.
export const DOC_TYPES = [
  { value: 'liquor_license',      label: 'Liquor license' },
  { value: 'food_handler_cert',   label: 'Food handler cert' },
  { value: 'business_license',    label: 'Business license' },
  { value: 'health_permit',       label: 'Health permit' },
  { value: 'insurance',           label: 'Insurance (general / liability)' },
  { value: 'workers_comp',        label: "Workers' comp" },
  { value: 'sales_tax',           label: 'Sales tax permit' },
  { value: 'fire_safety',         label: 'Fire safety inspection' },
  { value: 'music_license',       label: 'Music license (BMI/ASCAP/SESAC)' },
  { value: 'lease',               label: 'Lease' },
  { value: 'other',               label: 'Other' },
]

export const URGENCY_STYLES = {
  expired: {
    label: 'Expired',
    border: 'border-red-300',
    bg:     'bg-red-50',
    text:   'text-red-900',
    pill:   'bg-red-600 text-white',
  },
  expires_in_7d: {
    label: 'Expires this week',
    border: 'border-red-200',
    bg:     'bg-red-50/60',
    text:   'text-red-900',
    pill:   'bg-red-500 text-white',
  },
  expires_in_30d: {
    label: 'Expires within 30 days',
    border: 'border-amber-200',
    bg:     'bg-amber-50/60',
    text:   'text-amber-900',
    pill:   'bg-amber-500 text-white',
  },
  expires_in_90d: {
    label: 'Expires within 90 days',
    border: 'border-yellow-200',
    bg:     'bg-yellow-50/50',
    text:   'text-yellow-900',
    pill:   'bg-yellow-500 text-black',
  },
  active: {
    label: 'Active',
    border: 'border-emerald-200',
    bg:     'bg-white',
    text:   'text-black/80',
    pill:   'bg-emerald-100 text-emerald-900',
  },
  no_date: {
    label: 'No date set',
    border: 'border-black/10',
    bg:     'bg-white',
    text:   'text-black/65',
    pill:   'bg-black/10 text-black/70',
  },
}
